/**
 * NotificationsPanel Component
 *
 * Dropdown panel in navbar for displaying user notifications
 */

import React, { useState, useRef, useEffect } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { ErrorState } from '@/shared/components/ui/ErrorState/ErrorState'
import { ModalErrorBanner } from '@/shared/components/ui/ModalForm/ModalErrorBanner'
import { formatDistanceToNow } from '@/shared/utils/date'

import type { UserNotification } from '../api/alerts.api'
import { useNotifications } from '../hooks/useNotifications'

interface NotificationsPanelProps {
  className?: string
}

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ className = '' }) => {
  const { t } = useTranslation('shared')
  const [isOpen, setIsOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const {
    notifications,
    unreadCount,
    loading,
    error,
    loadError,
    unreadCountStatus,
    markAsRead,
    acknowledge,
    markAllAsRead,
    deleteNotification,
    refresh,
  } = useNotifications()

  // The count can be 0 for want of an answer (unavailable) or stale: in every state the rows
  // themselves also say whether something is unread, so «Marcar todas» never hides on a lie.
  const hasUnread = unreadCount > 0 || notifications.some((notification) => !notification.isRead)

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close panel when Escape key is pressed
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  // Refresh notifications when panel opens
  useEffect(() => {
    if (isOpen) {
      refresh()
    }
  }, [isOpen, refresh])

  const handleMarkAsRead = async (id: string) => {
    await markAsRead(id)
  }

  const handleAcknowledge = async (notification: UserNotification) => {
    const acknowledged = await acknowledge(notification.id)
    // Only navigate when the backend recorded the acknowledgement — otherwise
    // a swallowed API failure would look successful and lose the unread state.
    if (acknowledged && notification.actionUrl) {
      window.location.href = notification.actionUrl
    }
  }

  const handleDelete = async (id: string) => {
    await deleteNotification(id)
  }

  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-400 [html[data-theme=light]_&]:text-red-700 bg-red-400/10 border-red-400/30'
      case 'warning':
        return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30 [html[data-theme=light]_&]:text-yellow-800'
      default:
        return 'text-blue-400 bg-blue-400/10 border-blue-400/30 [html[data-theme=light]_&]:text-blue-700'
    }
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        )
      case 'warning':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )
    }
  }

  // Same retry card for both failures: a refresh over rows already shown, and a first load with none.
  const renderLoadError = (title: string, details: string | undefined) => (
    <ErrorState compact type="server" title={title} message={details} showRetry onRetry={refresh} />
  )

  return (
    // Positioned only from `sm` up. Below it the panel is anchored to the fixed
    // navbar instead of the bell, so it spans the screen with a 16 px gutter:
    // anchored right-0 to the bell, the 384 px panel opened at x = -155 on a
    // 320 px phone.
    <div className={`sm:relative ${className}`}>
      {/* Notification Button */}
      <button
        ref={buttonRef}
        data-testid="notifications-button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-crypto-muted hover:text-white transition-colors rounded-lg hover:bg-crypto-border/30"
        aria-label={
          unreadCountStatus === 'unavailable'
            ? t('alerts.notificationsLoadFailed', 'Notifications could not be loaded')
            : unreadCountStatus === 'stale'
              ? t('alerts.unreadCountStale', {
                  count: unreadCount,
                  defaultValue_one: '{{count}} unread notification; could not refresh',
                  defaultValue_other: '{{count}} unread notifications; could not refresh',
                })
              : t('alerts.notifications') || 'Notifications'
        }
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span
            data-testid="notifications-unread-badge"
            className="absolute -top-1 -right-1 bg-crypto-accent text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}

        {/* The count was never loaded and its request failed: say so instead of showing nothing */}
        {unreadCountStatus === 'unavailable' && (
          <span
            data-testid="notifications-load-failed-mark"
            aria-hidden="true"
            className="absolute -top-1 -right-1 text-sm font-bold leading-none text-red-400 [html[data-theme=light]_&]:text-red-700"
          >
            !
          </span>
        )}
      </button>

      {/* Notifications Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          data-testid="notifications-panel"
          className="absolute left-4 right-4 sm:left-auto sm:right-0 mt-2 w-auto sm:w-96 max-h-[600px] glass-effect rounded-xl shadow-2xl border border-crypto-border/50 bg-crypto-card flex flex-col z-50"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-crypto-border/50">
            <h3 className="text-lg font-semibold text-crypto-text">
              {t('alerts.notifications') || 'Notifications'}
            </h3>
            <div className="flex items-center gap-2">
              {hasUnread && (
                <button
                  data-testid="mark-all-read-button"
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-crypto-accent hover:text-crypto-accent-light transition-colors"
                >
                  {t('alerts.markAllAsRead') || 'Mark all read'}
                </button>
              )}
            </div>
          </div>

          {/* Inline error from failed notification actions (renders nothing when null).
              mx/mt only: an mb-* here would conflict with the banner's own mb-4. */}
          <ModalErrorBanner
            message={error}
            className="mx-4 mt-3"
            testId="notifications-error-banner"
          />

          {/* A refresh failed but the rows loaded before are still on screen */}
          {!loading && loadError && notifications.length > 0 && (
            <div data-testid="notifications-refresh-error" className="mx-4 mt-3 break-words">
              {renderLoadError(t('alerts.refreshFailed'), loadError.details)}
            </div>
          )}

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-crypto-accent" />
              </div>
            ) : loadError && notifications.length === 0 ? (
              <div data-testid="notifications-load-error" className="p-4 break-words">
                {renderLoadError(t('alerts.loadFailed'), loadError.details)}
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <svg
                  className="w-12 h-12 text-crypto-muted mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
                <p className="text-crypto-muted">
                  {t('alerts.noNotifications') || 'No notifications'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-crypto-border/30">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    data-testid={`notification-${notification.id}`}
                    className={`p-4 hover:bg-crypto-border/20 transition-colors ${
                      !notification.isRead ? 'bg-crypto-accent/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Severity Icon */}
                      <div
                        className={`flex-shrink-0 p-2 rounded-lg border ${getSeverityColor(
                          notification.severity,
                        )}`}
                      >
                        {getSeverityIcon(notification.severity)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-sm font-medium text-crypto-text truncate">
                            {notification.title}
                          </h4>
                          {!notification.isRead && (
                            <span className="flex-shrink-0 w-2 h-2 bg-crypto-accent rounded-full" />
                          )}
                        </div>
                        <p className="text-sm text-crypto-muted mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-crypto-muted mt-2">
                          {formatDistanceToNow(new Date(notification.createdAt))}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0 flex items-center gap-1">
                        {!notification.isRead && (
                          <button
                            data-testid={`mark-read-${notification.id}`}
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="p-1 text-crypto-muted hover:text-crypto-text transition-colors"
                            title={t('alerts.markAsRead') || 'Mark as read'}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </button>
                        )}
                        {notification.actionUrl && (
                          <button
                            data-testid={`acknowledge-${notification.id}`}
                            onClick={() => handleAcknowledge(notification)}
                            className="p-1 text-crypto-muted hover:text-crypto-accent transition-colors"
                            title={t('alerts.acknowledge') || 'View details'}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </button>
                        )}
                        <button
                          data-testid={`delete-${notification.id}`}
                          onClick={() => handleDelete(notification.id)}
                          className="p-1 text-crypto-muted hover:text-crypto-danger transition-colors"
                          title={t('alerts.delete') || 'Delete'}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-crypto-border/50 flex justify-center">
              <button
                onClick={() => setIsOpen(false)}
                className="text-sm text-crypto-accent hover:text-crypto-accent-light transition-colors"
              >
                {t('alerts.viewAllNotifications') || 'View all notifications'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

NotificationsPanel.displayName = 'NotificationsPanel'
